/**
 * Shared enums mirroring the server-side canonical enums under
 * `App\Lib\Constants\`. Values MUST match — they're wire tokens.
 */

export enum Roles {
  SUPER_ADMIN = 'super_admin',
  SYSTEM = 'system',
  TENANT_ADMIN = 'tenant_admin',
  MANAGER = 'manager',
  /**
   * Kiosk-only administration tier scoped to individual lockers via
   * `user_kiosk_assignments`. Cannot log in to the admin portal.
   */
  SUPERVISOR = 'supervisor',
  USER = 'user',
  GUEST = 'guest',
}

export enum Permissions {
  // Platform
  PLATFORM_TENANTS_MANAGE = 'platform.tenants.manage',
  PLATFORM_USERS_MANAGE = 'platform.users.manage',
  PLATFORM_VIEW_ALL = 'platform.view-all',
  // Gates the kiosk Stats "Hardware health" card — super_admin-only via
  // the platform-tier bypass in buildUser.ts (held by no tenant role).
  PLATFORM_KIOSKS_HEALTH_VIEW = 'platform.kiosks.health.view',
  // Gates the super-admin On-Prem Clients section (appliances, their tokens and server updates).
  PLATFORM_APPLIANCES_MANAGE = 'platform.appliances.manage',
  // Gates the super-admin MDM Profiles section (what a flash station can set a unit up for).
  PLATFORM_MDM_PROFILES_MANAGE = 'platform.mdm-profiles.manage',
  // Tenant settings
  TENANT_SETTINGS_MANAGE = 'tenant.settings.manage',
  // Tenant users / roles
  TENANT_USERS_VIEW = 'tenant.users.view',
  TENANT_USERS_MANAGE = 'tenant.users.manage',
  TENANT_ROLES_ASSIGN = 'tenant.roles.assign',
  // Tenant catalogs
  TENANT_GROUPS_VIEW = 'tenant.groups.view',
  TENANT_GROUPS_MANAGE = 'tenant.groups.manage',
  TENANT_DEVICES_VIEW = 'tenant.devices.view',
  TENANT_DEVICES_MANAGE = 'tenant.devices.manage',
  TENANT_KIOSKS_VIEW = 'tenant.kiosks.view',
  TENANT_KIOSKS_MANAGE = 'tenant.kiosks.manage',
  TENANT_LOCATIONS_VIEW = 'tenant.locations.view',
  TENANT_LOCATIONS_MANAGE = 'tenant.locations.manage',
  TENANT_TEMPLATES_VIEW = 'tenant.templates.view',
  TENANT_TEMPLATES_MANAGE = 'tenant.templates.manage',
  TENANT_BAY_ACCESS_VIEW = 'tenant.bay-access.view',
  TENANT_BAY_ACCESS_MANAGE = 'tenant.bay-access.manage',
  // Tenant operational
  TENANT_ORDERS_VIEW = 'tenant.orders.view',
  TENANT_ACTIVITY_VIEW = 'tenant.activity.view',
  TENANT_COMMANDS_ISSUE = 'tenant.commands.issue',
  TENANT_TRACKER_VIEW = 'tenant.tracker.view',
  // OTA app-update surface (release catalog + fleet dispatch).
  // tenant_admin-and-above only — higher-trust than the manager-tier
  // TENANT_COMMANDS_ISSUE. Mirrors backend tenantAdminGrants().
  TENANT_OTA_MANAGE = 'tenant.ota.manage',
}

export enum UserStatus {
  ACTIVE = 'active',
  DISABLED = 'disabled',
  DELETED = 'deleted',
}

export enum TenantStatus {
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  DELETED = 'deleted',
}

/** What an MDM profile says about the locker app. Mirrors `App\Lib\Constants\AssemblyAppInstall`. */
export enum AssemblyAppInstall {
  /** The bench decides whether the station installs the locker app. */
  OPTIONAL = 'optional',
  /** The customer's MDM installs it: the station never does. */
  FORBIDDEN = 'forbidden',
}

export enum TenantMembershipStatus {
  ACTIVE = 'active',
  DISABLED = 'disabled',
}

/**
 * Tenant-scoped access-type marker on `user_memberships.access_type`.
 * Encodes the operator's intent for how this user's access primarily
 * resolves. Only meaningful when the user holds the `user` role at
 * the tenant; other roles leave it null.
 *
 * Wire token = enum string. Must stay in sync with the BE
 * `MembershipAccessType` enum (`backend/app/Lib/Constants/`).
 */
export enum AccessType {
  /**
   * No access intent set — default for newly-created `user`
   * memberships. Operator hasn't picked a model yet; no bay or
   * group panel renders.
   */
  NONE = 'NONE',
  ALL_ACCESS = 'ALL_ACCESS',
  SELECTED_BAYS = 'SELECTED_BAYS',
  // No GROUPS: group membership is a SEPARATE always-on access axis
  // (its own control), not a direct-access mode. access_type only
  // selects the direct mode; a user reaches a bay via ANY path
  // (direct ∪ groups ∪ location).
}

export enum KioskStatus {
  ACTIVE = 'active',
  MAINTENANCE = 'maintenance',
  DECOMMISSIONED = 'decommissioned',
}

export enum DeviceState {
  ACTIVE = 'ACTIVE',
  /** Reported lost (terminal); recovered → ACTIVE. */
  LOST = 'LOST',
  /** Decommissioned; reinstate → ACTIVE. */
  RETIRED = 'RETIRED',
  // Damage group — the kiosk return-report conditions captured 1:1; each
  // locks the device, all reactivate → ACTIVE, and Mission Control + the
  // editor fold them under one "damaged" label.
  DAMAGED = 'DAMAGED',
  MISSING_PARTS = 'MISSING_PARTS',
  NOT_CHARGING = 'NOT_CHARGING',
  ABNORMAL = 'ABNORMAL',
}

/**
 * Persisted bay operational lifecycle. Mirrors the server-side
 * `App\Lib\Constants\BayOperationalState` PHP enum AND LockerApp's
 * `BayOperationalState` Kotlin enum — same 14 entries, UPPERCASE
 * tokens, wire-stable across all three layers.
 *
 * Distinct from the LED **presentation** state list in
 * `KioskControlSection.systemSwatches` (`BayState` in LockerApp).
 * That one is 11 entries + carries `door_open` (transient sensor
 * signal, not stored) and lacks `DEACTIVATED` / `OCCUPIED`.
 *
 * Operator targets (admin-settable, authoritative): `AVAILABLE`,
 * `DEACTIVATED`, `MAINTENANCE`, `SUSPENDED`. Workflow-driven states
 * (CHECKED_OUT, CHARGING, …) are ALSO offered by the picker — an admin
 * pick there is advisory (the next kiosk uplink may overwrite it), not
 * blocked. `PENDING_STOCK` is the one state never offered: it's
 * stock-driven + kiosk-owned (the kiosk holds a DC bay there when a
 * device is assigned but not stocked, and auto-clears to AVAILABLE on
 * restock), so it's omitted from the picker (and `AVAILABLE` is omitted
 * too while a bay is PENDING_STOCK). The picker guard lives in
 * `lib/utils/bayStateOptions.ts`.
 */
export enum BayOperationalState {
  INITIAL      = 'INITIAL',
  AVAILABLE    = 'AVAILABLE',
  CHECKED_OUT  = 'CHECKED_OUT',
  CHECKED_IN   = 'CHECKED_IN',
  CHARGING     = 'CHARGING',
  FULL_CHARGE  = 'FULL_CHARGE',
  DEACTIVATED  = 'DEACTIVATED',
  CLEANING     = 'CLEANING',
  SUSPENDED    = 'SUSPENDED',
  NOT_CONNECTED = 'NOT_CONNECTED',
  PENDING_STOCK = 'PENDING_STOCK',
  DELAYED      = 'DELAYED',
  MAINTENANCE  = 'MAINTENANCE',
  RESERVED     = 'RESERVED',
  OCCUPIED     = 'OCCUPIED',
}

/**
 * Per-bay operating mode, meaningful when the kiosk's
 * `operation.mode` is `hybrid` (each bay is individually AC or DC).
 * In a non-hybrid kiosk every bay is uniformly the kiosk's single
 * mode, so the per-bay control is hidden. Wire vocab matches the kiosk
 * `BayMode` on both sides.
 *
 * Two-valued by design. An inventory bay is a DC bay carrying
 * `is_inventory`, not a third mode — inventory is orthogonal to the
 * AC/DC journey split.
 */
export enum BayMode {
  AC = 'AC',
  DC = 'DC',
}

export enum OrderStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
  ERROR = 'ERROR',
  DELAYED = 'DELAYED',
  /** Server-only force-close by order reconciliation (kiosk lost the order); outcome unknown. */
  ORPHANED = 'ORPHANED',
}

export enum OrderTransactionKind {
  CHECKOUT = 'CHECKOUT',
  CHECKIN = 'CHECKIN',
  OPEN = 'OPEN',
  RESET = 'RESET',
  LOCK = 'LOCK',
  ALARM = 'ALARM',
  ERROR = 'ERROR',
  ADMIN_OVERRIDE = 'ADMIN_OVERRIDE',
  SYSTEM = 'SYSTEM',
  CANCEL = 'CANCEL',
}

export enum BayStateChangeTrigger {
  POLL = 'poll',
  ADMIN_LOCAL = 'admin_local',
  ADMIN_REMOTE = 'admin_remote',
  WORKFLOW = 'workflow',
  SYSTEM = 'system',
  RECOVERY = 'recovery',
}

export enum ActivityLogSeverity {
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

export enum ActivityLogSource {
  WEB = 'web',
  MQTT = 'mqtt',
  SYSTEM = 'system',
  KIOSK = 'kiosk',
}

export enum SyncStatus {
  SYNCED = 'synced',
  SYNCING = 'syncing',
  FAILED = 'failed',
  POSTPONED = 'postponed',
}

export enum MasterKeyTargetType {
  BAY = 'BAY',
  GROUP = 'GROUP',
  KIOSK = 'KIOSK',
}

export enum OrderAccessAuthorizationType {
  DIRECT_USER_GRANT = 'DIRECT_USER_GRANT',
  GROUP_INHERITED = 'GROUP_INHERITED',
  AC_PASSCODE = 'AC_PASSCODE',
}

/** Application-level error catalog — shown in ErrorPage view. */
export const Errors: Record<string, { title: string; errorCode: number; message: string }> = {
  NOT_FOUND: {
    title: 'Pixar was unable to find that page',
    errorCode: 404,
    message: "The page you're looking for doesn't exist. Check the URL or go back to the dashboard.",
  },
  UNAUTHORIZED: {
    title: 'Unauthorized',
    errorCode: 401,
    message: "You don't have permission to view this page.",
  },
  FORBIDDEN: {
    title: 'Forbidden',
    errorCode: 403,
    message: 'This action requires elevated permissions.',
  },
  UNKNOWN: {
    title: 'Unknown Error',
    errorCode: 500,
    message: 'Something went wrong on our end.',
  },
}
